import { ConsentType } from '__generated__/globalTypes';

export type GivenConsents = Partial<Record<ConsentType, boolean>>;

export type PiplConsentValues = {
  piplPrivacyPolicyConsentGiven: boolean | null;
  piplSensitiveDataConsentGiven: boolean | null;
  piplThirdPartySharingConsentGiven: boolean | null;
  piplCrossBorderTransferConsentGiven: boolean | null;
};

export type PiplConsentField = keyof PiplConsentValues;

export const USER_FIELD_BY_CONSENT_TYPE: Record<ConsentType, PiplConsentField> =
  {
    [ConsentType.PIPL_PRIVACY_POLICY]: 'piplPrivacyPolicyConsentGiven',
    [ConsentType.PIPL_SENSITIVE_DATA]: 'piplSensitiveDataConsentGiven',
    [ConsentType.PIPL_THIRD_PARTY_SHARING]: 'piplThirdPartySharingConsentGiven',
    [ConsentType.PIPL_CROSS_BORDER_TRANSFER]:
      'piplCrossBorderTransferConsentGiven',
  };

export const SEPARATE_CONSENT_INTRO_KEY =
  `logged_out:sign_up.forms.privacy_consent.pipl.separate_consent_intro` as const;

// Written out in full rather than built from a template so the keys stay
// literal, which is what i18next's typed lookup checks against.
export const LABEL_KEY_BY_FIELD = {
  explicitPrivacyConsentGiven:
    'logged_out:sign_up.health_data_consent_checkbox.label',
  explicitEEADataStorageConsentGiven:
    'logged_out:sign_up.storing_data_eea_checkbox.label',
  marketingOptIn: 'logged_out:sign_up.marketing_checkbox.label',
  piplPrivacyPolicyConsentGiven: `logged_out:sign_up.forms.privacy_consent.pipl.privacy_policy_checkbox.label`,
  piplSensitiveDataConsentGiven: `logged_out:sign_up.forms.privacy_consent.pipl.sensitive_data_checkbox.label`,
  piplThirdPartySharingConsentGiven: `logged_out:sign_up.forms.privacy_consent.pipl.third_party_sharing_checkbox.label`,
  piplCrossBorderTransferConsentGiven: `logged_out:sign_up.forms.privacy_consent.pipl.cross_border_transfer_checkbox.label`,
} as const;

export type ConsentField = keyof typeof LABEL_KEY_BY_FIELD;

export const toGivenConsents = (user: PiplConsentValues): GivenConsents =>
  Object.fromEntries(
    Object.entries(USER_FIELD_BY_CONSENT_TYPE).map(([consentType, field]) => [
      consentType,
      user[field] === true,
    ]),
  );

/**
 * A consent is outstanding unless it is explicitly given: null and false both
 * mean "never asked" or "declined", and both warrant a prompt.
 *
 * An unrecognised consent type is ignored: there is no copy for it, and
 * prompting would make adding one server-side a breaking change for old clients.
 */
export const getOutstandingConsents = (
  required: ConsentType[],
  given: GivenConsents,
): ConsentType[] =>
  required.filter(
    consentType =>
      consentType in USER_FIELD_BY_CONSENT_TYPE && given[consentType] !== true,
  );
